import styles from './DynamicFormBuilder.module.css';

const VALUE_TYPES = ['string', 'number', 'boolean'];

const INPUT_CLASS = 'form-control form-control-sm';
const SELECT_CLASS = 'form-select form-select-sm';

function typeOfValue(value) {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  return 'string';
}

function coerceValue(raw, kind) {
  if (kind === 'boolean') return raw === 'true' || raw === true;
  if (kind === 'number') {
    if (raw === '' || raw === null || raw === undefined) return 0;
    const n = Number(raw);
    return Number.isNaN(n) ? 0 : n;
  }
  return raw === null || raw === undefined ? '' : String(raw);
}

function defaultValueFor(kind) {
  if (kind === 'boolean') return false;
  if (kind === 'number') return 0;
  return '';
}

function renderValueInput(value, kind, onChange) {
  if (kind === 'boolean') {
    return (
      <select
        className={SELECT_CLASS}
        value={String(Boolean(value))}
        onChange={(e) => onChange(coerceValue(e.target.value, 'boolean'))}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (kind === 'number') {
    return (
      <input
        type="number"
        className={INPUT_CLASS}
        value={value ?? 0}
        onChange={(e) => onChange(coerceValue(e.target.value, 'number'))}
      />
    );
  }
  return (
    <input
      type="text"
      className={INPUT_CLASS}
      value={value ?? ''}
      onChange={(e) => onChange(coerceValue(e.target.value, 'string'))}
    />
  );
}

export default function OptionsEditor({ options, onChange }) {
  const rows = Array.isArray(options) ? options : [];

  const replace = (i, next) => {
    const updated = [...rows];
    updated[i] = next;
    onChange(updated);
  };

  const removeRow = (i) => {
    const updated = [...rows];
    updated.splice(i, 1);
    onChange(updated);
  };

  const move = (i, delta) => {
    const target = i + delta;
    if (target < 0 || target >= rows.length) return;
    const updated = [...rows];
    [updated[i], updated[target]] = [updated[target], updated[i]];
    onChange(updated);
  };

  const addRow = () => {
    onChange([...rows, { value: '', label: '', active: true }]);
  };

  return (
    <div className="d-flex flex-column gap-2 align-items-start">
      {rows.length === 0 ? (
        <p className="m-0 small text-muted fst-italic">No options yet.</p>
      ) : (
        <table className="table table-sm small mb-0 w-100">
          <thead>
            <tr>
              <th>Value type</th>
              <th>Value</th>
              <th>Label</th>
              <th>Active</th>
              <th aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const kind = typeOfValue(row.value);
              const isFirst = i === 0;
              const isLast = i === rows.length - 1;
              return (
                <tr key={i}>
                  <td>
                    <select
                      className={SELECT_CLASS}
                      value={kind}
                      onChange={(e) => replace(i, { ...row, value: coerceValue(row.value, e.target.value) || defaultValueFor(e.target.value) })}
                    >
                      {VALUE_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td>{renderValueInput(row.value, kind, (v) => replace(i, { ...row, value: v }))}</td>
                  <td>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={row.label ?? ''}
                      onChange={(e) => replace(i, { ...row, label: e.target.value })}
                    />
                  </td>
                  <td className="text-center">
                    <div className="checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={row.active !== false}
                          onChange={(e) => replace(i, { ...row, active: e.target.checked })}
                        />
                        <span className="visually-hidden">Active</span>
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-1 text-nowrap">
                      <button type="button" className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`} title="Move up" disabled={isFirst} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`} title="Move down" disabled={isLast} onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className={`btn btn-sm btn-outline-danger ${styles.iconButton}`} title="Remove option" onClick={() => removeRow(i)}>×</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addRow}>+ Add option</button>
    </div>
  );
}
